/**
 * [MEMEET-VFX] 2026-11-05 — Arka plan efektleri (bulanıklık + arka plan görseli).
 *
 * Kişiyi arka plandan ayırmak için LiveKit'in @livekit/track-processors paketi (MediaPipe selfie
 * segmentasyonu) kullanılır; işlem tamamen tarayıcıda yapılır, görüntü dışarı gitmez. Paket ve
 * MediaPipe dosyaları YALNIZ bir efekt seçildiğinde dinamik olarak yüklenir (ana paket büyümez).
 * MediaPipe wasm + model dosyaları dış CDN'den değil uygulamanın kendi `mediapipe/` klasöründen
 * sunulur (sahip kararı 2026-11-05).
 *
 * Bu dosya standalone (lodos-me-meet), masaüstü (memeet-core-ui) ve monorepo (socialfolders/MeMeet)
 * kopyalarında BİREBİR aynıdır.
 */

export type VideoEffect =
  | { mode: "none" }
  | { mode: "blur"; level: "light" | "strong" }
  | { mode: "image"; imageId: string };

export interface BackgroundImage {
  id: string;
  /** Uygulamanın kendi klasöründeki dosya adı (memeet-backgrounds/<file>) ya da veri URL'si (kendi görseli). */
  file?: string;
  dataUrl?: string;
  labelKey: string;
  label: string;
}

export const BLUR_RADIUS = { light: 6, strong: 18 } as const;

/** Hazır arka planlar — dosyalar uygulamanın public/memeet-backgrounds klasöründe. */
export const BUILTIN_BACKGROUNDS: BackgroundImage[] = [
  { id: "mist", file: "mist.jpg", labelKey: "memeetBgMist", label: "Sis" },
  { id: "night", file: "night.jpg", labelKey: "memeetBgNight", label: "Gece" },
  { id: "sunset", file: "sunset.jpg", labelKey: "memeetBgSunset", label: "Gün batımı" },
  { id: "ocean", file: "ocean.jpg", labelKey: "memeetBgOcean", label: "Okyanus" },
  { id: "forest", file: "forest.jpg", labelKey: "memeetBgForest", label: "Orman" },
  { id: "studio", file: "studio.jpg", labelKey: "memeetBgStudio", label: "Stüdyo" },
];

export const CUSTOM_IMAGE_ID = "custom";
const PREF_EFFECT = "memeet.videoEffect.v1";
const PREF_CUSTOM = "memeet.videoEffect.customImage.v1";
const CUSTOM_MAX_SIDE = 1280;

/** Uygulamanın kendi statik dosyaları için taban adres. Web: sitenin kökü; masaüstü (file://): sayfanın yanı. */
export function memeetAssetBase(): string {
  try {
    const w = window as unknown as { __MEMEET_ASSET_BASE__?: string };
    if (w.__MEMEET_ASSET_BASE__) return w.__MEMEET_ASSET_BASE__.replace(/\/?$/, "/");
    if (location.protocol === "http:" || location.protocol === "https:") return `${location.origin}/`;
    return new URL("./", document.baseURI).href;
  } catch {
    return "/";
  }
}

export function readVideoEffect(): VideoEffect {
  try {
    const raw = localStorage.getItem(PREF_EFFECT);
    if (!raw) return { mode: "none" };
    const v = JSON.parse(raw) as VideoEffect;
    if (v?.mode === "blur" && (v.level === "light" || v.level === "strong")) return v;
    if (v?.mode === "image" && typeof v.imageId === "string") {
      if (v.imageId === CUSTOM_IMAGE_ID && !readCustomImage()) return { mode: "none" };
      if (v.imageId !== CUSTOM_IMAGE_ID && !BUILTIN_BACKGROUNDS.some((b) => b.id === v.imageId)) return { mode: "none" };
      return v;
    }
  } catch {
    /* depolama yok/bozuk: efektsiz */
  }
  return { mode: "none" };
}

export function writeVideoEffect(v: VideoEffect) {
  try { localStorage.setItem(PREF_EFFECT, JSON.stringify(v)); } catch { /* yok say */ }
}

export function readCustomImage(): string | null {
  try { return localStorage.getItem(PREF_CUSTOM); } catch { return null; }
}

/** Kullanıcının görselini en uzun kenar 1280 px JPEG'e küçültüp saklar; veri URL'sini döner. */
export async function saveCustomImage(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("image_load_failed"));
      i.src = url;
    });
    const scale = Math.min(1, CUSTOM_MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.max(1, Math.round(img.naturalWidth * scale));
    c.height = Math.max(1, Math.round(img.naturalHeight * scale));
    c.getContext("2d")?.drawImage(img, 0, 0, c.width, c.height);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    try { localStorage.setItem(PREF_CUSTOM, dataUrl); } catch { /* kota dolu: yalniz bu oturum */ }
    return dataUrl;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function backgroundImageUrl(imageId: string): string | null {
  if (imageId === CUSTOM_IMAGE_ID) return readCustomImage();
  const b = BUILTIN_BACKGROUNDS.find((x) => x.id === imageId);
  return b?.file ? `${memeetAssetBase()}memeet-backgrounds/${b.file}` : null;
}

// ── İşleyici (dinamik yükleme) ──────────────────────────────────────────────

type TP = typeof import("@livekit/track-processors");
let tpPromise: Promise<TP> | null = null;
function loadTP(): Promise<TP> {
  if (!tpPromise) tpPromise = import("@livekit/track-processors").catch((e) => { tpPromise = null; throw e; });
  return tpPromise;
}

let supportCache: boolean | null = null;
/** Tarayıcı arka plan işleyicisini destekliyor mu (paket yüklenmeden kaba kontrol + yüklenince kesin). */
export async function videoEffectsSupported(): Promise<boolean> {
  if (supportCache !== null) return supportCache;
  try {
    const tp = await loadTP();
    supportCache = tp.supportsBackgroundProcessors();
  } catch {
    supportCache = false;
  }
  return supportCache;
}

function assetPaths() {
  const base = memeetAssetBase();
  return { tasksVisionFileSet: `${base}mediapipe/wasm`, modelAssetPath: `${base}mediapipe/selfie_segmenter.tflite` };
}

/** LocalVideoTrack benzeri: setProcessor / stopProcessor / getProcessor. */
interface ProcessableTrack {
  setProcessor: (p: any, showProcessedStreamLocally?: boolean) => Promise<void>;
  stopProcessor: (keepElement?: boolean) => Promise<void>;
  getProcessor: () => any;
}

/**
 * Efekti bir LiveKit LocalVideoTrack'ine uygular. Var olan işleyici varsa çalışırken geçiş yapar
 * (görüntü titremez); "none" ise işleyiciyi durdurur. Hata fırlatmaz: false döner.
 */
export async function applyVideoEffect(track: ProcessableTrack | null | undefined, effect: VideoEffect): Promise<boolean> {
  if (!track) return false;
  try {
    const current = track.getProcessor?.();
    if (effect.mode === "none") {
      if (current) await track.stopProcessor();
      return true;
    }
    const tp = await loadTP();
    if (!tp.supportsBackgroundProcessors()) return false;
    const opts =
      effect.mode === "blur"
        ? { mode: "background-blur" as const, blurRadius: BLUR_RADIUS[effect.level] }
        : { mode: "virtual-background" as const, imagePath: backgroundImageUrl(effect.imageId) || "" };
    if (effect.mode === "image" && !opts.imagePath) {
      if (current) await track.stopProcessor();
      return false;
    }
    if (current && typeof current.switchTo === "function" && current.name === "background-processor") {
      await current.switchTo(opts);
      return true;
    }
    if (current) await track.stopProcessor();
    const processor = tp.BackgroundProcessor({ ...opts, assetPaths: assetPaths() } as any);
    await track.setProcessor(processor);
    return true;
  } catch (e) {
    console.warn("[MeMeet] arka plan efekti uygulanamadi:", e);
    return false;
  }
}
